import { useState } from "react";

import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@buildpy/ui/dialog";
import { TemplateItem } from "@buildpy/core/modules/template/types";
import { lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";

interface Props {
  tradeName: string;
  /** 기본 템플릿의 이 공종 트리. 비어 있으면 버튼이 열리기만 하고 안내를 띄운다. */
  items: TemplateItem[];
  onAdd: (items: TemplateItem[]) => void;
  // 폰 공종 카드 바닥처럼 여는 단추가 남은 폭을 나눠 가질 때만.
  triggerFill?: boolean;
  triggerLabel?: string;
  // 잠긴 견적 — 여는 단추를 끄고, 열려 있던 창은 닫고, 「추가」 를 보내지 않는다.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 잠긴 직후·재렌더 전에 눌린 「추가」 도 버린다.
  isLocked: () => boolean;
}

/**
 * 템플릿에서 항목 가져오기. 단가는 넘기지 않는다 — 이름·단위만 가져오고
 * 금액은 이번 견적에서 다시 정한다(자동완성이 단가표에서 채워 준다).
 */
const TemplatePicker = ({ tradeName, items, onAdd, triggerFill = false, triggerLabel = "템플릿에서 추가", disabled = false, isLocked }: Props) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const toggle = (index: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const add = unlessLocked(lockedNow(disabled, isLocked), () => {
    const chosen = items.filter((_, index) => picked.has(index));
    if (chosen.length > 0) onAdd(chosen);
    setPicked(new Set());
    setOpen(false);
  });

  return (
    <Dialog
      open={openUnlessLocked(open, disabled)}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPicked(new Set());
      }}>
      <DialogTrigger asChild>
        <Button className={triggerFill ? "flex-1" : undefined} disabled={disabled} size="sm" variant="outline">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tradeName} — 템플릿 항목</DialogTitle>
          <DialogDescription>
            고를 항목을 체크하세요. 단가는 비운 채로 들어갑니다.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
          {items.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              이 공종의 템플릿 항목이 없습니다.
            </p>
          )}
          {items.map((item, index) => (
            <label
              key={`${item.scope}|${item.item}|${index}`}
              className="flex cursor-pointer items-start gap-x-2 border-b border-border px-3 py-2 last:border-0 hover:bg-muted/50">
              <Checkbox
                checked={picked.has(index)}
                className="mt-1"
                onCheckedChange={() => toggle(index)}
              />
              <span className="flex flex-col">
                <span className="text-sm">{item.item}</span>
                <span className="text-xs text-muted-foreground">
                  {item.scope || "구분 없음"} · {item.unit || "단위 없음"}
                  {item.qty_expr ? ` · 수량 ${item.qty_expr}` : ""}
                  {item.children && item.children.length > 0
                    ? ` · SET ${item.children.length}개`
                    : ""}
                </span>
              </span>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button
            disabled={picked.size === 0 || disabled}
            size="sm"
            variant="primary"
            onClick={add}>
            {picked.size}개 추가
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePicker;
